import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { Identity, IdentityListData } from '../../core/model/identity/identity';
import { Subscription } from 'rxjs';
import moment from 'moment';
import { Helpers } from '../../core/helpers';
import { Paging } from '../../core/model/paging/paging';
import { Router } from '@angular/router';
import { IdentityService } from '../../core/service/api/identity.service';
import { LocationsService } from '../../core/service/api/location.service';
import { LocationData } from '../../core/model/location/location-data';
import {
	LocationS,
	LocationSListData,
} from '../../core/model/location/location';
import { LocationsList } from '../../core/model/location/location-list';
import { Client } from '../../core/model/client/client';
import { NzTableQueryParams } from 'ng-zorro-antd/table';
import { ClientsCounters } from '../../core/model/client/counters';
import { ClientService } from '../../core/service/api/client.service';
import { IdentityMockService } from '../../core/service/mock-api/identity.service';
import { DeviceAccount } from '../../core/model/identity/device-account';
import { CaptiveDeviceAccount } from '../../core/model/identity/captive-device-account';
import { NzMessageService } from 'ng-zorro-antd/message';
import { GeneralKonstants } from '../../core/model/general_k/general_k';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { selectFilterState } from '../../core/store/filter/filter.selectors';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ServiceTypeEnum } from '../../core/model/enum/service-type.enum';

type IdentityFilterState = {
	location?: string;
	location_id: string;
	since: string;
	until: string;
	time_range: string;
};

@Component({
	selector: 'app-identity',
	templateUrl: './identity.component.html',
	styleUrl: './identity.component.scss',
})
export class IdentityComponent implements OnInit, OnDestroy {
	private _subscriptions: Array<Subscription> = [];
	private filterSubscription: Subscription | undefined;
	_since: string = moment()
		.subtract(1, 'month')
		.startOf('month')
		.format('YYYY-MM-DD');
	_until: string = moment()
		.subtract(1, 'month')
		.endOf('month')
		.format('YYYY-MM-DD');
	_location_id: string = '';
	_locationName: string = '';
	_periodToShow: string = '';
	_identities: Array<Identity> = [];
	_clients: Array<any> = new Array<any>(); // MOCK DATA
	_filteredClients: Array<any> = new Array<any>();
	_locationList: Array<LocationsList> = new Array<LocationsList>();
	_deviceAccounts: Array<DeviceAccount> = new Array<DeviceAccount>();
	_captiveDeviceAccounts: Array<CaptiveDeviceAccount> =
		new Array<CaptiveDeviceAccount>();
	_deviceAccountsMode: 'passpoint' | 'captive' | '' = '';
	_strategyFilter: 'all' | 'passpoint' | 'captive' = 'all';
	_totalCounters: number = 0;
	_verifiedCounters: number = 0;
	_marketingConsentCounters: number = 0;
	_profileConsentCounters: number = 0;
	_searchTerm: string = '';
	_allClients: Array<Identity> = [];
	// Table
	_paging: Paging = new Paging();
	_loading: boolean = false;
	_totalItems: number = 0;
	_isFirst: boolean = true;
	// End Table
	_showDetailsFromBlur: boolean = false;
	_time_range: string = '';
	loader: boolean = false;
	_isVisibleUntilModal: boolean = false;
	Helpers = Helpers;
	_passpoint = ServiceTypeEnum.PASSPOINT;
	_captive = ServiceTypeEnum.CAPTIVE;

	GeneralKonstants = GeneralKonstants;
	private _expandedRowId: string | null = null;
	constructor(
		private _locationsService: LocationsService,
		private _identityService: IdentityService,
		private _router: Router,
		private _locationService: LocationsService,
		private _clientService: ClientService,
		private _nzMessageService: NzMessageService,
		private _translate: TranslateService,
		private store: Store,

	) { }

	ngOnInit() {
		const persistedFilter = this.getPersistedFilterState();
		if (this.hasRequiredFilters(persistedFilter)) {
			this.applyFilterState(persistedFilter, true);
		}

		this.filterSubscription = this.store
			.select(selectFilterState)
			.pipe(
				debounceTime(100),
				distinctUntilChanged(
					(prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
				),
			)
			.subscribe({
				next: (filter) => {
					if (!filter || !this.hasRequiredFilters(filter)) return;
					this.applyFilterState(filter, this.isParamsChanged(filter));
				},
			});
	}

	private isParamsChanged(filter: Partial<IdentityFilterState>): boolean {
		return (
			this._since !== (filter.since || '') ||
			this._until !== (filter.until || '') ||
			this._location_id !== (filter.location_id || '') ||
			this._time_range !== (filter.time_range || '')
		);
	}

	private applyFilterState(
		filter: Partial<IdentityFilterState>,
		shouldFetch: boolean,
	): void {
		this._locationName = filter.location || '';
		this._location_id = filter.location_id || '';
		this._since = filter.since || '';
		this._until = filter.until || '';
		this._time_range = filter.time_range || '';

		if (!shouldFetch) {
			return;
		}

		this._periodToShow = '';
		this._paging.pageIndex = 1;
		this.AllLocatiosList();
		this.GetIdentities();
		this.GETClientsPasspointCount();
	}

	private hasRequiredFilters(filter: Partial<IdentityFilterState> | null | undefined): boolean {
		return Boolean(
			filter?.location_id &&
			filter?.since &&
			filter?.until &&
			filter?.time_range,
		);
	}

	private getPersistedFilterState(): IdentityFilterState {
		return {
			location: Helpers.getLocationNameInLS(),
			location_id:
				localStorage.getItem('location_id_selected') ||
				Helpers.getDefaultLocation(),
			since: Helpers.getSince(),
			until: Helpers.getUntil(),
			time_range: Helpers.getSavedTimeRange() || Helpers.getRangeMode(),
		};
	}

	private AllLocatiosList(): void {
		if (!this.hasRequiredFilters(this.getCurrentFilterState())) {
			return;
		}

		this._subscriptions.push(
			this._locationService
				.GETLocations(
					Helpers.getDefaultLocationGroup(),
					this._time_range,
					this._since,
					this._until,
				)
				.subscribe({
					next: (response: LocationSListData | any) => {
						this._locationList = response?.data || [];
					},
					error: (err: any) => {
						throw new Error(err);
					},
				}),
		);
	}
	private GETClientsPasspointCount(): void {
		if (!this.hasRequiredFilters(this.getCurrentFilterState())) {
			this.setLoader(false);
			return;
		}

		const { since, until } = this.getIdentityDateRange();
		this.setLoader(true);
		this._subscriptions.push(
			this._identityService
				.GETPasspointCount(since, until, this._location_id)
				.subscribe({
					next: (response: ClientsCounters | any) => {
						if (!response?.data) {
							this._totalCounters = 0;
							this._verifiedCounters = 0;
							this._marketingConsentCounters = 0;
							this._profileConsentCounters = 0;
							this.setLoader(false);
							return;
						}
						this._totalCounters = response.data.totals;
						this._verifiedCounters = response.data.verified;
						this._marketingConsentCounters = response.data.marketing_consent;
						this._profileConsentCounters = response.data.profile_consent;
						this.setLoader(false);
					},
					error: (err: any) => {
						this.setLoader(false);
						throw new Error(err);
					},
				}),
		);
	}
	private setLoader(val: boolean) {
		this.loader = val;
	}

	showSessionClient(client: Client): void {
		this._router.navigate(['/client_sessions'], {
			queryParams: { client_id: client.id, type: 'passpoint' },
		});
	}
	toggleDetailsFromBlur(): void {
		this._showDetailsFromBlur = !this._showDetailsFromBlur;
	}

	//table
	hasData<K extends keyof Client>(field: K): boolean {
		return this._filteredClients.some(
			(client) =>
				client[field] !== null &&
				client[field] !== undefined &&
				client[field] !== '' &&
				client[field] !== 0,
		);
	}

	hasDataDevice(field: string): boolean {
		const activeDeviceAccounts =
			this._deviceAccountsMode === 'captive'
				? this._captiveDeviceAccounts
				: this._deviceAccounts;
		return activeDeviceAccounts.some(
			(device) =>
				device?.[field as keyof typeof device] !== null &&
				device?.[field as keyof typeof device] !== undefined &&
				device?.[field as keyof typeof device] !== '' &&
				device?.[field as keyof typeof device] !== 0,
		);
	}
	onStrategyFilterChange(value: 'all' | 'passpoint' | 'captive'): void {
		this._strategyFilter = value;
		this._paging.pageIndex = 1;
		this.applyFiltersAndPagination();
	}
	onPageIndexChange($event: number) {
		this._paging.pageIndex = $event;
		this._paging.pageSize = 10;
		this.applyFiltersAndPagination();
	}

	onQueryParamsChange(params: NzTableQueryParams): void {
		const { pageSize, pageIndex, sort } = params;
		this._paging.sort = sort;
		this._paging.pageSize = pageSize;
		this._paging.pageIndex = pageIndex;
		if (!this._isFirst) {
			this.applyFiltersAndPagination();
		}
		this._isFirst = false;
	}

	resetTable() {
		this._paging.pageIndex = 1;
		this._paging.sort = [];
		this._isFirst = true;
	}
	//end table

	private getIdentityDateRange(): { since: string; until: string } {
		const yesterday = new Date();
		yesterday.setDate(yesterday.getDate() - 1);
		const isUntilYesterday =
			this._until === yesterday.toISOString().split('T')[0];

		if (!isUntilYesterday) {
			return {
				since: this._since,
				until: this._until,
			};
		}

		const today = new Date();
		const sinceDate = new Date(this._since);
		sinceDate.setDate(sinceDate.getDate() + 1);

		return {
			since: sinceDate.toISOString().split('T')[0],
			until: today.toISOString().split('T')[0],
		};
	}

	private normalizeDeviceAccounts<T>(data: T[] | T | { data?: T[] | T } | null | undefined): T[] {
		if (Array.isArray(data)) {
			return data as T[];
		}

		if (data && typeof data === 'object' && 'data' in data) {
			const nestedData = (data as { data?: T[] | T }).data;
			if (Array.isArray(nestedData)) {
				return nestedData as T[];
			}
			return nestedData ? [nestedData as T] : [];
		}

		return data ? [data as T] : [];
	}

	private mapClientsWithExpand(clients: Array<Identity>): Array<Identity> {
		return clients.map((client) => ({
			...client,
			expand: false,
		}));
	}

	private getClientsByStrategy(clients: Array<Identity>): Array<Identity> {
		if (this._strategyFilter === 'all') {
			return [...clients];
		}

		return clients.filter((client) => {
			const strategy = (client.strategy || '').toLowerCase();
			return this._strategyFilter === 'passpoint'
				? strategy === GeneralKonstants.Passpoint
				: strategy !== GeneralKonstants.Passpoint;
		});
	}

	private paginateClients(clients: Array<Identity>): Array<Identity> {
		const startIndex = (this._paging.pageIndex - 1) * this._paging.pageSize;
		return clients.slice(startIndex, startIndex + this._paging.pageSize);
	}

	private getSearchFilteredClients(clients: Array<Identity>): Array<Identity> {
		const q = this._searchTerm.toLocaleLowerCase().trim();
		if (!q) {
			return [...clients];
		}

		return clients.filter((item) =>
			[
				item.id,
				item.email,
				item.first_name,
				item.last_name,
				item.phone_number,
			]
				.filter(Boolean)
				.some((value) => String(value).toLocaleLowerCase().includes(q)),
		);
	}

	private getSortField(sortKey: string | null): keyof Identity | null {
		if (!sortKey) {
			return null;
		}

		const supportedSortFields: Array<keyof Identity> = [
			'last_update',
			'registered_at',
		];

		return supportedSortFields.includes(sortKey as keyof Identity)
			? (sortKey as keyof Identity)
			: null;
	}

	private getSortedClients(clients: Array<Identity>): Array<Identity> {
		const activeSort = this._paging.sort?.find((item) => item.value);
		const sortField = this.getSortField(activeSort?.key || null);
		if (!activeSort?.value || !sortField) {
			return [...clients];
		}

		const sortDirection = activeSort.value === 'ascend' ? 1 : -1;
		return [...clients].sort((left, right) => {
			const leftValue = left[sortField] ?? 0;
			const rightValue = right[sortField] ?? 0;

			if (leftValue === rightValue) {
				return 0;
			}

			return leftValue > rightValue ? sortDirection : -sortDirection;
		});
	}

	private applyFiltersAndPagination(): void {
		const strategyFilteredClients = this.getClientsByStrategy(this._allClients);
		const searchFilteredClients =
			this.getSearchFilteredClients(strategyFilteredClients);
		const sortedClients = this.getSortedClients(searchFilteredClients);
		const totalItems = sortedClients.length;
		const totalPages = totalItems
			? Math.ceil(totalItems / this._paging.pageSize)
			: 1;

		if (this._paging.pageIndex > totalPages) {
			this._paging.pageIndex = totalPages;
		}

		const paginatedClients = this.paginateClients(sortedClients);
		this.setVisibleClients(paginatedClients, totalItems);
	}

	private setVisibleClients(clients: Array<Identity>, totalItems?: number): void {
		this._clients = clients;
		this._filteredClients = [...clients];
		if (typeof totalItems === 'number') {
			this._totalItems = totalItems;
		}

		if (
			this._expandedRowId &&
			!this._filteredClients.some((client) => client.id === this._expandedRowId)
		) {
			this._expandedRowId = null;
			this._deviceAccounts = [];
			this._captiveDeviceAccounts = [];
			this._deviceAccountsMode = '';
		}
	}

	private updateIdentityPeriodToShow(since: string, until: string): void {
		const formatDate = (dateString: string): string => {
			const date = new Date(dateString);
			const options: Intl.DateTimeFormatOptions = {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
			};
			return date.toLocaleDateString('it-IT', options);
		};

		const originalRange = since === this._since && until === this._until;
		this._periodToShow = originalRange
			? ''
			: `${formatDate(since)} - ${formatDate(until)}`;
	}

	private GetIdentities(): void {
		if (!this.hasRequiredFilters(this.getCurrentFilterState())) {
			this._loading = false;
			this._allClients = [];
			this.setVisibleClients([], 0);
			return;
		}

		const { since, until } = this.getIdentityDateRange();
		this.updateIdentityPeriodToShow(since, until);
		this._loading = true;

		this._subscriptions.push(
			this._identityService
				.GETIdentitiesAll(since, until, this._location_id)
				.subscribe({
					next: (response: IdentityListData | any) => {
						if (!response?.data) {
							this._allClients = [];
							this.setVisibleClients([], 0);
							this._loading = false;
							return;
						}
						this._allClients = this.mapClientsWithExpand(response.data || []);
						this.applyFiltersAndPagination();
						this._loading = false;
					},
					error: (err: any) => {
						this._loading = false;
						throw new Error(err);
					},
				}),
		);
	}

	// aggiung ere anche registered_at/last_update
	exportToCSV(): void {
		const { since, until } = this.getIdentityDateRange();
		this.updateIdentityPeriodToShow(since, until);
		this._loading = true;
		this._subscriptions.push(
			this._identityService
				.GETIdentitiesAll(since, until, this._location_id)
				.subscribe({
					next: (response: IdentityListData | any) => {
						this._loading = false;

						if (!response || !response.data || response.data.length === 0) {
							this._nzMessageService.warning(
								this._translate.instant('alert.warning.canCSVExported'),
							);
							return;
						}

						const csvData = response.data.map((client: any) => ({
							Username: client.username || '',
							Email: client.email || '',
							Phone: client.phone || '',
							'First Name': client.first_name || '',
							'Last Name': client.last_name || '',
							'Marketing Consent':
								client.marketing_consent !== null &&
									client.marketing_consent !== undefined
									? client.marketing_consent
										? 'true'
										: 'false'
									: '',
							'Profile Consent':
								client.profile_consents !== null &&
									client.profile_consents !== undefined
									? client.profile_consents
										? 'true'
										: 'false'
									: '',
							Verified:
								client.verified !== null && client.verified !== undefined
									? client.verified
										? 'true'
										: 'false'
									: '',
							'Registered At': client.registered_at
								? moment(client.registered_at).format('YYYY-MM-DD HH:mm:ss')
								: '',
							// 'Last Session': client.last_update ? moment(client.last_update).format('YYYY-MM-DD HH:mm:ss') : ''
						}));

						const csvHeaders = Object.keys(csvData[0]);
						const csvContent = [
							csvHeaders.join(','),
							...csvData.map((row: (typeof csvData)[0]) =>
								csvHeaders
									.map((header) => `"${row[header as keyof typeof row]}"`)
									.join(','),
							),
						].join('\n');

						const blob = new Blob([csvContent], {
							type: 'text/csv;charset=utf-8;',
						});
						const link = document.createElement('a');
						const url = URL.createObjectURL(blob);
						link.setAttribute('href', url);
						link.setAttribute(
							'download',
							`identities_${moment().format('YYYY-MM-DD_HH-mm-ss')}.csv`,
						);
						link.style.visibility = 'hidden';
						document.body.appendChild(link);
						link.click();
						document.body.removeChild(link);

						this._nzMessageService.info(
							this._translate.instant('alert.success.canCSVExported'),
						);
					},
					error: (err: any) => {
						this._loading = false;
						this._nzMessageService.error(
							this._translate.instant('alert.error.canCSVExported'),
						);
						throw new Error(err);
					},
				}),
		);
	}

	// Gestisce la chiusura e l'apertura indipendente della row
	// Carica i dati del profilo del cliente
	showProfileFromIdentity(row: Identity): void {
		if (this._expandedRowId === row.id) {
			const currentExpandedRow = this._clients.find(
				(client) => client.id === this._expandedRowId,
			);
			if (currentExpandedRow) {
				currentExpandedRow.expand = false;
			}
			this._expandedRowId = null;
			this._deviceAccounts = [];
			this._captiveDeviceAccounts = [];
			this._deviceAccountsMode = '';
			return;
		}
		if (this._expandedRowId !== null) {
			const currentExpandedRow = this._clients.find(
				(client) => client.id === this._expandedRowId,
			);
			if (currentExpandedRow) {
				currentExpandedRow.expand = false;
			}
		}
		this._expandedRowId = row.id;
		this._deviceAccounts = [];
		this._captiveDeviceAccounts = [];
		const strategy = (row.strategy || '').toLowerCase();
		this._deviceAccountsMode =
			strategy === GeneralKonstants.Passpoint ? 'passpoint' : 'captive';
		const newExpandedRow = this._clients.find((client) => client.id === row.id);
		if (newExpandedRow) {
			newExpandedRow.expand = true;
		}
		this._newIdentityID = row.id;
		if (this._deviceAccountsMode === 'passpoint') {
			this._subscriptions.push(
				this._identityService.GETDeviceAccounts(row.id).subscribe({
					next: (response: DeviceAccount | any) => {
						this._deviceAccounts = this.normalizeDeviceAccounts<DeviceAccount>(
							response?.data,
						);
					},
					error: (err: any) => {
						throw new Error(err);
					},
				}),
			);
		} else {
			this._subscriptions.push(
				this._identityService.GETDeviceAccountsCaptive(row.id).subscribe({
					next: (response: any) => {
						this._captiveDeviceAccounts =
							this.normalizeDeviceAccounts<CaptiveDeviceAccount>(
								response?.data,
							);
					},
					error: (err: any) => {
						throw new Error(err);
					},
				}),
			);
		}
	}
	checkIfStrategyAreSameForAllClients(): boolean {
		if (this._clients.length > 0) {
			const firstClient = this._clients[0];
			return this._clients.every(
				(client) => client.strategy === firstClient.strategy,
			);
		}
		return false;
	}
	checkIfLocationAreSameForAllClients(): boolean {
		if (this._clients.length > 0) {
			const firstClient = this._clients[0];
			return this._clients.every(
				(client) => client._location === firstClient._location,
			);
		}
		return false;
	}

	search(): void {
		this._paging.pageIndex = 1;
		this.applyFiltersAndPagination();
	}
	// Modal Until
	identityDateUntil: Date | null = null;
	_newIdentityDateUntil: string = '';
	_newProfileID: string = '';
	_newUsername: string = '';
	_newIdentityID: string = '';
	_newServiceType: ServiceTypeEnum = ServiceTypeEnum.PASSPOINT;
	handleCancelUntilModal(): void {
		this._isVisibleUntilModal = false;
	}
	handleOkUntilModal(): void {
		var body = {
			until: this._newIdentityDateUntil,
		};
		this._isVisibleUntilModal = false;
		this._subscriptions.push(
			this._identityService
				.PUTDeviceUntil(this._newIdentityID, this._newUsername, body)
				.subscribe({
					next: (response: DeviceAccount | any) => {
						if (response.success) {
							this._nzMessageService.success(
								this._translate.instant('alert.success.untilDateUpdate'),
							);
							this.GetIdentities();
						} else {
							this._nzMessageService.error(
								this._translate.instant('alert.error.untilDateUpdate'),
							);
							throw new Error(response);
						}
					},
					error: (err: any) => {
						this._nzMessageService.error(
							this._translate.instant('alert.error.untilDateUpdate'),
						);
						throw new Error(err);
					},
				}),
		);
	}
	openUntilModal(_deviceAccounts: DeviceAccount): void {
		this._isVisibleUntilModal = true;
		this.identityDateUntil = _deviceAccounts.valid_until
			? new Date(_deviceAccounts.valid_until)
			: null;
		this._newIdentityDateUntil = _deviceAccounts.valid_until
			? moment(_deviceAccounts.valid_until).format('YYYY-MM-DD')
			: '';
		this._newUsername = _deviceAccounts.username;
		// this._newProfileID = _deviceAccounts.;
	}
	onChangeUntildate(event: any): void {
		this._newIdentityDateUntil = event
			? moment(event).format('YYYY-MM-DD')
			: '';
	}


	// End Modal Until

	prepareDisableDevice(username: string, serviceType: ServiceTypeEnum): void {
		this._newUsername = username;
		this._newServiceType = serviceType;
	}

	confirmDisable(): void {
		this._subscriptions.push(
			this._identityService
				.PUTDisableSingleDevice(
					this._newIdentityID,
					this._newUsername,
					this._newServiceType,
				)
				.subscribe({
					next: (response: DeviceAccount | any) => {
						if (response.success) {
							this._nzMessageService.success(
								this._translate.instant('modal.success.disable_profile'),
							);
							this.GetIdentities();
						} else {
							this._nzMessageService.error(
								this._translate.instant('modal.error.disable_profile'),
							);
							throw new Error(response);
						}
					},
					error: (err: any) => {
						this._nzMessageService.error(
							this._translate.instant('modal.error.disable_profile'),
						);
						throw new Error(err);
					},
				}),
		);
	}

	goTo(username: string, where: string, locationID?: string, identity_id?: string): void {
		switch (where) {
			case 'sessions':
				this._router.navigate(['/troubleshooting/sessions/'], {
					queryParams: { username: username, locationID: locationID, method: this._deviceAccountsMode, identity_id: identity_id },
				});
				break;
			case 'logs':
				this._router.navigate(['/troubleshooting/logs/'], {
					queryParams: { username: username, locationID: locationID, method: this._deviceAccountsMode, identity_id: identity_id },
				});
				break;

			case 'sessions-page':
				this._router.navigate(['/sessions-page/'], {
					queryParams: { username: username, identity_id: identity_id },
				})
				break;
			default:
				break;
		}
	}

	cancelDisable(): void { }

	updateUntildateProfile(identity: Identity) { }

	private getCurrentFilterState(): IdentityFilterState {
		return {
			location: this._locationName,
			location_id: this._location_id,
			since: this._since,
			until: this._until,
			time_range: this._time_range,
		};
	}

	ngOnDestroy(): void {
		this._subscriptions.forEach((sub) => sub.unsubscribe());
		if (this.filterSubscription) this.filterSubscription.unsubscribe();
	}
}
